import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import GroupMountingService from '@/services/GroupMountingService'
import type { IGroupMountingFilter } from '@/models/filters/groupMountingFilter'
import type { IGroupMountingApiResponse } from '@/models/api/groupMountingApiResponse'
import { compareGroupKeys } from '@/composables/CompteurGroupingComposable'

/**
 * Groupes de monte du bloc PARC du tableau des compteurs.
 *
 * Les lignes G1/G2/G3 étaient figées : toujours affichées, même sans plan de monte, et un G4 ou plus
 * (le plan peut compter jusqu'à 8 groupes) restait invisible alors que « Nb Verrats PDM » comptait
 * ses verrats. Elles suivent désormais le plan de monte sélectionné (P.D.M, dernier RAZ du jour) :
 * aucun plan, aucune ligne de groupe.
 */

/**
 * Libellés `G{n}` des lignes de groupe du Parc, triés par numéro.
 *
 * - les groupes du plan de monte sélectionné, même sans verrat (ligne à zéro) ;
 * - plus les créneaux des lignes Baseline déjà présentes (`G{n}`, cf. `CompteurRebuildService`) :
 *   un groupe créé après le chargement de l'écran porte déjà ses verrats et ne doit pas les perdre.
 *
 * @param planGroupNumbers numéros des groupes du plan sélectionné
 * @param baselineCreneaux clés de `parcNbVerratByGroupe` (« » si une ligne n'a pas de créneau)
 */
export function parcGroupLabels(planGroupNumbers: Iterable<number>, baselineCreneaux: Iterable<string>): string[] {
    const labels = new Set<string>()
    for (const number of planGroupNumbers) {
        if (Number.isFinite(number)) labels.add(`G${number}`)
    }
    for (const creneau of baselineCreneaux) {
        if (creneau !== '') labels.add(creneau)
    }
    const numero = (label: string) => label.replace(/^G/i, '')
    return [...labels].sort((a, b) => compareGroupKeys(numero(a), numero(b)))
}

/**
 * Numéros des groupes du plan de monte donné, relus à chaque changement de plan.
 *
 * Appel direct du service et non `groupMountingStore.load` : ce store est partagé avec l'écran du
 * plan de monte et `load` y REMPLACE `datas` ; le charger d'ici changerait ce que ce dernier affiche.
 * Aucun plan (null / 0) : aucun appel, aucun groupe. Une réponse qui arrive après un changement de
 * plan est ignorée (jeton de requête).
 */
export function useParcPlanGroups(mountingPlanId: Ref<number | null | undefined>) {
    const planGroupNumbers = ref<number[]>([])
    let request = 0

    watch(mountingPlanId, async (planId) => {
        const current = ++request
        planGroupNumbers.value = []
        if (!(planId != null && planId > 0)) return

        const result = await GroupMountingService.getAll<IGroupMountingFilter, IGroupMountingApiResponse>(
            { page: 1, pageSize: 0, mountingPlanId: planId })
        if (current !== request || result.error) return
        planGroupNumbers.value = (result.data as IGroupMountingApiResponse[]).map((g) => g.number)
    }, { immediate: true })

    return { planGroupNumbers: computed(() => planGroupNumbers.value) }
}
